import React, { Component } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { getActiveClients } from 'modules/clients/clients.actions';
import { GET_ACTIVE_CLIENTS_SUCCESS } from 'modules/clients/types';
import { GET_ALL_ENUMERATIONS_SUCCESS } from 'modules/enumerations/store/enumeationTypes';
import { getAllEnumerations } from 'modules/enumerations/store/enumerationActions';
import { GET_EMAIL_TEMPLATES_SUMMARIES_SUCCESS } from 'modules/emailTemplates/store/emailTemplateTypes';
import { getEmailTemplateSummaries } from 'modules/emailTemplates/store/emailTemplate.actions';
import { TASK_TRIGGER_LETTERS_GROUP_ID } from 'modules/enumerations/enumerationIds';
import { getToday } from 'utilities/common';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import RuleFormComponent from './components/RuleFormComponent.component';
import { buildCutoffCondition, getLetterFields, isLetterRule, joinLetterConditions } from './letterRules';
import {
  getTaskRule,
  createTaskRule,
  updateTaskRule,
  getRuleConditions,
  addRuleCondition,
  deleteRuleCondition,
  getRuleClients,
  addRuleClient,
  deleteRuleClient,
  getValidTriggerFields,
  getTaskTriggerServices,
  probeSegmentTables,
  getReloPoliciesByClient,
  resetRuleForm,
  clearValidTriggerFields,
} from './taskRules.actions';
import {
  GET_TASK_RULE_SUCCESS,
  CREATE_TASK_RULE_SUCCESS,
  UPDATE_TASK_RULE_SUCCESS,
  GET_RULE_CONDITIONS_SUCCESS,
  ADD_RULE_CONDITION_SUCCESS,
  DELETE_RULE_CONDITION_SUCCESS,
  GET_RULE_CLIENTS_SUCCESS,
  ADD_RULE_CLIENT_SUCCESS,
  DELETE_RULE_CLIENT_SUCCESS,
  GET_VALID_TRIGGER_FIELDS_SUCCESS,
  GET_TASK_TRIGGER_SERVICES_SUCCESS,
} from './taskRules.types';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

class RuleFormContainer extends Component {
  static propTypes = {
    match: ReactRouterPropTypes.match.isRequired,
    history: ReactRouterPropTypes.history.isRequired,
    setPageTitle: PropTypes.func.isRequired,
    showToast: PropTypes.func.isRequired,
    getActiveClients: PropTypes.func.isRequired,
    getAllEnumerations: PropTypes.func.isRequired,
    getEmailTemplateSummaries: PropTypes.func.isRequired,
    getTaskRule: PropTypes.func.isRequired,
    createTaskRule: PropTypes.func.isRequired,
    updateTaskRule: PropTypes.func.isRequired,
    getRuleConditions: PropTypes.func.isRequired,
    addRuleCondition: PropTypes.func.isRequired,
    deleteRuleCondition: PropTypes.func.isRequired,
    getRuleClients: PropTypes.func.isRequired,
    addRuleClient: PropTypes.func.isRequired,
    deleteRuleClient: PropTypes.func.isRequired,
    getValidTriggerFields: PropTypes.func.isRequired,
    getTaskTriggerServices: PropTypes.func.isRequired,
    probeSegmentTables: PropTypes.func.isRequired,
    getReloPoliciesByClient: PropTypes.func.isRequired,
    resetRuleForm: PropTypes.func.isRequired,
    clearValidTriggerFields: PropTypes.func.isRequired,
    currentRule: PropTypes.object,
    currentConditions: PropTypes.array,
    currentClients: PropTypes.array,
    validTriggerFields: PropTypes.array,
    isLoadingTriggerFields: PropTypes.bool,
    triggerServices: PropTypes.array,
    segmentCapableServices: PropTypes.object,
    userId: PropTypes.number,
    allEnums: PropTypes.object,
    emailTemplates: PropTypes.array,
  };

  static defaultProps = {
    segmentCapableServices: {},
    emailTemplates: [],
  };

  state = {
    isLoading: true,
    isSaving: false,
    isSegmentProbeDone: false,
    allClients: [],
    letterTemplates: [], // TODO: Load from templates module
    ruleData: {
      ruleName: '',
      description: '',
      triggerServiceId: null,
      isSegmentLevel: false,
      taskTypeId: null,
      taskImportanceId: null,
      serviceId: null,
      regardingId: null,
      letterTemplateId: null,
      dueDateDaysOffset: 0,
      dueDateBasedOnTrigger: false,
      dueDateTriggerFieldId: null,
      dueDateTriggerFieldName: null,
      applyToAllClients: true,
      reloPolicyId: null, // BACKEND REQUIREMENT: Need to add this field to task rule model
      isActive: false,
    },
    conditions: [],
    selectedClients: [],
  };

  componentDidMount() {
    this.loadInitialData();
  }

  componentWillUnmount() {
    this.props.resetRuleForm();
  }

  loadInitialData = async () => {
    const { match } = this.props;
    const ruleId = match.params.id;

    this.props.setPageTitle(ruleId ? 'Edit Task Rule' : 'Create Task Rule');

    // Enumerations (task types, importance, operators, service categories) - needed for deep links
    const enumResult = await this.props.getAllEnumerations();
    if (enumResult.type !== GET_ALL_ENUMERATIONS_SUCCESS) {
      this.props.showToast('Failed to load enumerations', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Trigger services (Task.TaskTriggerServiceGroup catalog)
    const servicesResult = await this.props.getTaskTriggerServices();
    if (servicesResult.type !== GET_TASK_TRIGGER_SERVICES_SUCCESS) {
      this.props.showToast('Failed to load trigger services', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Which services can be used at Segment Level (derived from the existing fields endpoint, no API change)
    await this.probeSegmentServices(servicesResult.response || []);

    // Clients must be loaded BEFORE the existing rule so client inclusions can be resolved
    const clientsResult = await this.props.getActiveClients();
    if (clientsResult.type === GET_ACTIVE_CLIENTS_SUCCESS) {
      this.setState({ allClients: clientsResult.response || [] });
    }

    // TODO: Load letter templates from templates module

    const templatesResult = await this.props.getEmailTemplateSummaries();
    if (templatesResult.type !== GET_EMAIL_TEMPLATES_SUMMARIES_SUCCESS) {
      this.props.showToast('Failed to load letter templates', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    if (ruleId) {
      await this.loadExistingRule(parseInt(ruleId, 10));
    }

    this.setState({ isLoading: false });
  };

  // Asks the trigger-fields endpoint, at segment level, for every active service not probed yet in this session.
  // A service is segment-capable when the answer contains any non-File field. Requests run in parallel; a failed
  // probe leaves the service undecided (and therefore still listed) rather than hiding it.
  probeSegmentServices = async (services) => {
    const { segmentCapableServices } = this.props;
    const pending = services
      .filter(s => s.isActive && segmentCapableServices[s.taskTriggerServiceGroupId] === undefined)
      .map(s => this.props.probeSegmentTables(s.taskTriggerServiceGroupId));

    await Promise.all(pending);
    this.setState({ isSegmentProbeDone: true });
  };

  isServiceAllowedAtLevel = (triggerServiceId, isSegmentLevel) => {
    if (triggerServiceId === null || !isSegmentLevel) {
      return true;
    }
    return this.props.segmentCapableServices[triggerServiceId] !== false;
  };

  loadExistingRule = async (ruleId) => {
    // Load rule
    const ruleResult = await this.props.getTaskRule(ruleId);
    if (ruleResult.type === GET_TASK_RULE_SUCCESS) {
      this.setState(prev => ({ ruleData: { ...prev.ruleData, ...ruleResult.response } }));
      // Trigger fetch of valid fields based on existing rule's service/level
      if (ruleResult.response.triggerServiceId !== null) {
        await this.fetchValidTriggerFields(
          ruleResult.response.triggerServiceId,
          ruleResult.response.isSegmentLevel
        );
      }
    }

    // Load conditions (their Table column is derived from each condition's field by the condition builder)
    const conditionsResult = await this.props.getRuleConditions(ruleId);
    if (conditionsResult.type === GET_RULE_CONDITIONS_SUCCESS) {
      this.setState({ conditions: conditionsResult.response || [] });
    }

    // Load client inclusions
    const clientsResult = await this.props.getRuleClients(ruleId);
    if (clientsResult.type === GET_RULE_CLIENTS_SUCCESS) {
      const clientInclusions = clientsResult.response || [];
      // Inclusion.clientId holds the Client.Record key, which activeClients exposes as clientRecordId
      const selectedClientIds = clientInclusions.map(c => c.clientId);
      this.setState(prev => ({
        selectedClients: prev.allClients.filter(c => selectedClientIds.includes(c.clientRecordId)),
      }));
    }
  };

  fetchValidTriggerFields = async (triggerServiceId, isSegmentLevel) => {
    const result = await this.props.getValidTriggerFields(triggerServiceId, isSegmentLevel);
    if (result.type !== GET_VALID_TRIGGER_FIELDS_SUCCESS) {
      this.props.showToast('Failed to load trigger fields', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
    return result;
  };

  // "Letter sent" rules are Segment Level rules on the Letters trigger group (one task per letter send), always due a
  // number of days after the send. A new letter rule starts with the "sent on or after today" cutoff so letters sent
  // before the rule existed do not create tasks; the user can move it earlier or clear it to backfill.
  handleTriggerTypeChange = async (isLetter) => {
    if (isLetter === isLetterRule(this.state.ruleData)) {
      return;
    }

    this.setState(prev => ({
      ruleData: {
        ...prev.ruleData,
        triggerServiceId: isLetter ? TASK_TRIGGER_LETTERS_GROUP_ID : null,
        isSegmentLevel: isLetter,
        dueDateBasedOnTrigger: isLetter,
        dueDateTriggerFieldId: null,
        dueDateTriggerFieldName: null,
        dueDateDaysOffset: isLetter ? Math.max(0, prev.ruleData.dueDateDaysOffset || 0) : prev.ruleData.dueDateDaysOffset,
      },
      conditions: [],
    }));

    if (!isLetter) {
      this.props.clearValidTriggerFields();
      return;
    }

    const result = await this.fetchValidTriggerFields(TASK_TRIGGER_LETTERS_GROUP_ID, true);
    const { sentDateField } = getLetterFields(result.response || []);
    if (sentDateField) {
      this.setState(prev => ({
        ruleData: {
          ...prev.ruleData,
          dueDateTriggerFieldId: sentDateField.validTriggerFieldId,
          dueDateTriggerFieldName: sentDateField.fieldName,
        },
        conditions: joinLetterConditions({
          letterCondition: null,
          cutoffCondition: buildCutoffCondition(sentDateField, getToday()),
          additionalConditions: [],
        }),
      }));
    }
  };

  // Functional setState: the due-date select issues two onChange calls in one event
  handleFieldChange = (field, value) => {
    this.setState(prev => ({
      ruleData: {
        ...prev.ruleData,
        [field]: value,
      },
    }));
  };

  handleServiceLevelChange = async (field, value) => {
    const newValue = value === '' ? null : value;
    const { ruleData } = this.state;

    if (ruleData[field] === newValue) {
      return;
    }

    const requestedServiceId = field === 'triggerServiceId' ? newValue : ruleData.triggerServiceId;
    const newIsSegmentLevel = field === 'isSegmentLevel' ? newValue : ruleData.isSegmentLevel;

    // Switching to Segment Level while a file-only service is selected drops the service (the picker no longer offers it)
    const newServiceId = this.isServiceAllowedAtLevel(requestedServiceId, newIsSegmentLevel) ? requestedServiceId : null;

    // Changing the service or level invalidates the field catalog: clear conditions and the due-date field
    this.setState(prev => ({
      ruleData: {
        ...prev.ruleData,
        [field]: newValue,
        triggerServiceId: newServiceId,
        dueDateTriggerFieldId: null,
        dueDateTriggerFieldName: null,
      },
      conditions: [],
    }));

    if (newServiceId !== null) {
      await this.fetchValidTriggerFields(newServiceId, newIsSegmentLevel);
    } else {
      this.props.clearValidTriggerFields();
    }
  };

  handleConditionsChange = (newConditions) => {
    this.setState({ conditions: newConditions });
  };

  handleClientsChange = ({ applyToAllClients, selectedClients, reloPolicyId }) => {
    this.setState(prev => ({
      ruleData: {
        ...prev.ruleData,
        applyToAllClients,
        reloPolicyId: reloPolicyId !== undefined ? reloPolicyId : prev.ruleData.reloPolicyId,
      },
      selectedClients,
    }));
  };

  handleSave = async (activate) => {
    this.setState({ isSaving: true });

    try {
      const { ruleData, conditions, selectedClients } = this.state;
      const { userId, match } = this.props;
      const isEditMode = !!match.params.id;

      // Step 1: Create or update rule
      const rulePayload = {
        ...ruleData,
        isActive: activate,
        addedBy: userId ?? 0,
        addedOn: isEditMode ? this.state.ruleData.addedOn : new Date(),
        modifiedBy: userId ?? 0,
        modifiedOn: new Date(),
      };

      let ruleResult;
      let taskRuleId;

      if (isEditMode) {
        taskRuleId = parseInt(match.params.id, 10);
        ruleResult = await this.props.updateTaskRule(taskRuleId, rulePayload);
        if (ruleResult.type !== UPDATE_TASK_RULE_SUCCESS) {
          throw new Error(ruleResult.messages?.[0] || 'Failed to update rule');
        }
      } else {
        ruleResult = await this.props.createTaskRule(rulePayload);
        if (ruleResult.type !== CREATE_TASK_RULE_SUCCESS) {
          throw new Error(ruleResult.messages?.[0] || 'Failed to create rule');
        }
        taskRuleId = ruleResult.response.taskRuleId;
      }

      // Step 2: Update conditions (delete all existing, add new ones)
      if (isEditMode) {
        // Delete existing conditions
        const existingConditions = this.props.currentConditions || [];
        for (const condition of existingConditions) {
          const deleteResult = await this.props.deleteRuleCondition(condition.taskRuleTriggerConditionId);
          if (deleteResult.type !== DELETE_RULE_CONDITION_SUCCESS) {
            throw new Error(deleteResult.messages?.[0] || 'Failed to remove an existing trigger condition');
          }
        }
      }

      // Add new conditions (the UI-only Table selection is not part of the payload; the field id identifies the table)
      for (const condition of conditions) {
        const conditionPayload = {
          validTriggerFieldId: condition.validTriggerFieldId,
          triggerFieldName: condition.triggerFieldName,
          comparisonOperatorId: condition.comparisonOperatorId,
          comparisonValue: condition.comparisonValue,
          conditionOrder: condition.conditionOrder,
          addedBy: userId ?? 0,
          modifiedBy: userId ?? 0,
        };
        const addResult = await this.props.addRuleCondition(taskRuleId, conditionPayload);
        if (addResult.type !== ADD_RULE_CONDITION_SUCCESS) {
          throw new Error(addResult.messages?.[0] || 'Failed to save trigger condition');
        }
      }

      // Step 3: Update client inclusions (delete all existing, add new ones)
      if (isEditMode) {
        const existingClients = this.props.currentClients || [];
        for (const client of existingClients) {
          const deleteResult = await this.props.deleteRuleClient(client.taskRuleClientInclusionId);
          if (deleteResult.type !== DELETE_RULE_CLIENT_SUCCESS) {
            throw new Error(deleteResult.messages?.[0] || 'Failed to remove an existing client inclusion');
          }
        }
      }

      // Add new client inclusions
      const isExclusion = ruleData.applyToAllClients; // Whitelist mode = false, blacklist mode = true
      for (const client of selectedClients) {
        const clientPayload = {
          clientId: client.clientRecordId,
          isExclusion,
          addedBy: userId ?? 0,
        };
        const addResult = await this.props.addRuleClient(taskRuleId, clientPayload);
        if (addResult.type !== ADD_RULE_CLIENT_SUCCESS) {
          throw new Error(addResult.messages?.[0] || 'Failed to save client inclusion');
        }
      }

      // Success!
      this.props.showToast(
        `Rule ${isEditMode ? 'updated' : 'created'} successfully${activate ? ' and activated' : ''}`,
        { severity: TOAST_MESSAGE_SEVERITY_SUCCESS }
      );

      this.props.history.push('/admin/task-rules');
    } catch (error) {
      this.props.showToast(error.message || 'Failed to save rule', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
      this.setState({ isSaving: false });
    }
  };

  handleFetchReloPolicies = (clientId) => {
    return this.props.getReloPoliciesByClient(clientId);
  };

  handleCancel = () => {
    this.props.history.push('/admin/task-rules');
  };

  render() {
    const {
      isLoading, isSaving, isSegmentProbeDone, ruleData, conditions, selectedClients, allClients, letterTemplates,
    } = this.state;
    const {
      validTriggerFields, isLoadingTriggerFields, triggerServices, segmentCapableServices, allEnums, match, emailTemplates, history,
    } = this.props;

    if (!hasPermission(Permissions.Admin.Tasks.Edit)) {
      history.push('/admin/task-rules');
      return null;
    }

    if (isLoading) {
      return <FullscreenSpinner />;
    }

    return (
      <RuleFormComponent
        ruleData={ruleData}
        conditions={conditions}
        clients={selectedClients}
        validTriggerFields={validTriggerFields}
        triggerServices={triggerServices}
        segmentCapableServices={segmentCapableServices}
        isSegmentProbeDone={isSegmentProbeDone}
        allClients={allClients}
        letterTemplates={letterTemplates}
        emailTemplates={emailTemplates}
        isLoadingTriggerFields={isLoadingTriggerFields}
        onChange={this.handleFieldChange}
        onConditionsChange={this.handleConditionsChange}
        onClientsChange={this.handleClientsChange}
        onFetchReloPolicies={this.handleFetchReloPolicies}
        onServiceLevelChange={this.handleServiceLevelChange}
        onTriggerTypeChange={this.handleTriggerTypeChange}
        onSave={this.handleSave}
        onCancel={this.handleCancel}
        isLoading={isSaving}
        isEditMode={!!match.params.id}
        allEnums={allEnums}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  currentRule: state.taskRules.currentRule,
  currentConditions: state.taskRules.currentConditions,
  currentClients: state.taskRules.currentClients,
  validTriggerFields: state.taskRules.validTriggerFields,
  isLoadingTriggerFields: state.taskRules.isLoadingTriggerFields,
  triggerServices: state.taskRules.triggerServices,
  segmentCapableServices: state.taskRules.segmentCapableServices,
  userId: state.auth.profile?.internalUserId,
  allEnums: state.enumerations.allEnums,
  emailTemplates: state.templates.emailTemplates,
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    setPageTitle,
    showToast,
    getActiveClients,
    getAllEnumerations,
    getEmailTemplateSummaries,
    getTaskRule,
    createTaskRule,
    updateTaskRule,
    getRuleConditions,
    addRuleCondition,
    deleteRuleCondition,
    getRuleClients,
    addRuleClient,
    deleteRuleClient,
    getValidTriggerFields,
    getTaskTriggerServices,
    probeSegmentTables,
    getReloPoliciesByClient,
    resetRuleForm,
    clearValidTriggerFields,
  })
)(RuleFormContainer);
